import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "chart.js/auto";
import { Line } from "react-chartjs-2";
import moment from "moment";
import QueryBlock from "../../components/QueryBlock";
import annotationPlugin from "chartjs-plugin-annotation";
import { Chart as ChartJS } from "chart.js";
import { CircularProgress } from "@mui/material";
import { useSnackbar } from "notistack";
import { getPeopleCountLog } from "../../apis/PeopleCount.Api";

ChartJS.register(annotationPlugin);

const FootfallLineChart = ({ selectedBuilding }) => {
    const userData = useSelector((state) => state.userData.data);
    const devices = useSelector((state) => state.device.data);
    const rooms = useSelector((state) => state.room.data);

    const { enqueueSnackbar } = useSnackbar();

    const [queryStartDate, setQueryStartDate] = useState("");
    const [queryEndDate, setQueryEndDate] = useState("");
    const [queryGender, setQueryGender] = useState("ALL");
    const [queryRooms, setQueryRooms] = useState([]);
    const [statistics, setStatistics] = useState({
        total: 0,
        average: 0,
        max: 0,
        min: 0,
    });
    const [labels, setLabels] = useState([]);
    const [datasets, setDatasets] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    async function handleSubmit() {
        setIsLoading(true);
        const roomToDevice = {};
        const deviceToRoom = {};

        let realRooms =
            Array.isArray(queryRooms) && queryRooms.length > 0
                ? [...queryRooms]
                : Object.values(rooms)
                      .filter((room) => room.buildingId === selectedBuilding)
                      .map((room) => room.roomId);

        realRooms.sort((a, b) => {
            const aRawFloor = a.split("-")[1];
            const bRawFloor = b.split("-")[1];

            let aFloor = aRawFloor?.replace("B", "-") || "";
            let bFloor = bRawFloor?.replace("B", "-") || "";
            if (aFloor.endsWith("F")) {
                aFloor = aFloor.slice(0, -1);
            }

            if (bFloor.endsWith("F")) {
                bFloor = bFloor.slice(0, -1);
            }

            if (Number(bFloor) === Number(aFloor)) {
                const aRawGender = a.split("-")[2];

                if (aRawGender === "M") {
                    return 1;
                } else {
                    return -1;
                }
            }

            return aRawFloor?.startsWith("B") && bRawFloor?.startsWith("B")
                ? Number(bFloor) - Number(aFloor)
                : Number(aFloor) - Number(bFloor);
        });

        realRooms =
            queryGender === "ALL"
                ? [...realRooms]
                : [...realRooms].filter((roomId) => {
                      const splitArr = roomId.split("-");
                      return splitArr[2]?.startsWith(queryGender[0]);
                  });

        realRooms.forEach((roomId) => {
            const device = Object.values(devices).find((device) => device.roomId === roomId);
            roomToDevice[roomId] = device?.deviceId;
            deviceToRoom[device?.deviceId] = roomId;
        });
        try {
            const res = await getPeopleCountLog({
                device_id: JSON.stringify(Object.values(roomToDevice)),
                start_time: moment(moment(queryStartDate, "YYYYMMDD").format("YYYY/MM/DD") + " 00:00:00.000").format(
                    "YYMMDDHHmmssSSS"
                ),
                end: moment(moment(queryEndDate, "YYYYMMDD").format("YYYY/MM/DD") + " 23:59:59.999").format(
                    "YYMMDDHHmmssSSS"
                ),
            });

            if (!res.data.indicator) {
                enqueueSnackbar(res.data.message, { variant: "error" });
                setIsLoading(false);
                return;
            }

            // device_id room_id current_count entry update_time
            const counts = res.data.message;
            const chartData = {};
            realRooms.forEach((roomId) => {
                chartData[roomId] = { intervals: new Array(24).fill(0), data: [] };
            });

            for (let count of counts) {
                // filter
                const rid = count.room_id;
                if (rooms[rid].buildingId !== selectedBuilding) continue;
                const time = moment(count.update_time, "YYMMDDHHmmssSSS").toDate();
                const hour = time.getHours();
                chartData[rid]["intervals"][hour] += count.entry;
            }

            const newLabels = [];
            for (let i = 0; i < 24; i++) {
                const time = i.toString().padStart(2, "0") + ":00";
                realRooms.forEach((roomId) => {
                    chartData[roomId].data.push({
                        time: time,
                        count: chartData[roomId].intervals[i],
                        range: i,
                    });
                });
                newLabels.push(time);
            }

            setLabels(newLabels);
            setDatasets(
                realRooms.map((roomId) => ({
                    label: roomId,
                    data: chartData[roomId].data.map((item) => item.count),
                    fill: false,
                }))
            );

            const margeArray = [];
            realRooms.forEach((roomId) => {
                margeArray.push(...chartData[roomId].data.map((item) => item.count));
            });
            const total = margeArray.reduce((a, b) => a + b, 0);
            setStatistics({
                total: total,
                average: (total / margeArray.length).toFixed(2),
                max: Math.max(...margeArray),
                min: Math.min(...margeArray),
            });
        } catch (error) {
            console.log(error);
            enqueueSnackbar("Failed to get data", { variant: "error" });
        }

        setIsLoading(false);
    }

    useEffect(() => {
        setLabels([]);
        setDatasets([]);
        setStatistics({
            total: 0,
            average: 0,
            max: 0,
            min: 0,
        });
    }, [selectedBuilding]);

    return (
        <div className="footfall-line-chart">
            <QueryBlock
                selectedBuilding={selectedBuilding}
                enableQueryHour={false}
                enableQuerySensors={false}
                queryStartDate={queryStartDate}
                setQueryStartDate={setQueryStartDate}
                queryEndDate={queryEndDate}
                setQueryEndDate={setQueryEndDate}
                queryGender={queryGender}
                setQueryGender={setQueryGender}
                queryRooms={queryRooms}
                setQueryRooms={setQueryRooms}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
            />
            <div className="chart-graph">
                <div className="chart-graph-content">
                    {isLoading ? (
                        <CircularProgress color="inherit" />
                    ) : (
                        <Line
                            data={{
                                labels: labels,
                                datasets: datasets,
                            }}
                            options={{
                                scales: {
                                    y: {
                                        beginAtZero: true,
                                    },
                                },
                                plugins: {
                                    annotation: {
                                        annotations: {
                                            line1: {
                                                type: "line",
                                                yMin: statistics.average,
                                                yMax: statistics.average,
                                                value: statistics.average,
                                                borderColor: "gray",
                                                borderWidth: 2,
                                                borderDash: [5, 5],
                                                label: {
                                                    content: "Average",
                                                    enabled: true,
                                                },
                                            },
                                        },
                                    },
                                },
                            }}
                        />
                    )}
                </div>
                <div className="chart-graph-statistics">
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Total</div>
                        <div className="chart-graph-statistics-block-number">{statistics.total.toLocaleString()}</div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Average</div>
                        <div className="chart-graph-statistics-block-number">{statistics.average.toLocaleString()}</div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Max</div>
                        <div className="chart-graph-statistics-block-number">{statistics.max.toLocaleString()}</div>
                    </div>
                    <div className="chart-graph-statistics-block">
                        <div className="chart-graph-statistics-block-item">Min</div>
                        <div className="chart-graph-statistics-block-number">{statistics.min.toLocaleString()}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FootfallLineChart;
